export interface IInitiateConsentRequest {
  bankId: number;
  agreedToBudgetRules: boolean;
}

export interface IAcquireConsentRequest {
  ledgerConsentId: number;
}

export interface IInitiateConsentResponse {
  consentUrl: string;
  clientId: string;
  invitationKey: number;
  locale: string;
}

export enum BudgetState {
  InsufficientPlan = "InsufficientPlan",
  UnpaidPlan = "UnpaidPlan",
  NoConsents = "NoConsents",
  ExpiredConsent = "ExpiredConsent",
  Ready = "Ready",
}

export enum TransactionState {
  /**
   * 2 hours passed but backend is not updating, should not be handled by frontend
   */
  RenewalAvailable = "RenewalAvailable",
  /**
   * Backend is updating accounts
   */
  RenewalPending = "RenewalPending",
  /**
   * Everything is up to date
   */
  UpToDate = "UpToDate",
  /**
   * Insufficient plan or plan is unpaid
   */
  CannotUpdate = "CannotUpdate",
}

export interface IBudgetServiceMetadata {
  isBudgetUser: boolean;
}

export interface IBudgetMetadata {
  budgetState: BudgetState;
  transactionState: TransactionState;
  newBankAccountsExist: boolean;
  pendingConsentsExist: boolean;
  areAllBankAccountsHidden: boolean;
  hasHiddenExpensePlanBankAccounts: boolean;
  canCreateOwnCategories: boolean;
}

export interface IBudgetTransactionMetadataRequest {
  bankAccountId?: number;
}

export interface IBudgetTransactionMetadata {
  firstTransaction: string | null;
}

export interface IBudgetSummary {
  bankAccounts: IBankAccount[];
}

export enum BankName {
  Swedbank = "Swedbank",
  Luminor = "Luminor",
  SEB = "SEB",
  Citadele = "Citadele",
  Revolut = "Revolut",
  LHV = "LHV",
  Coop = "Coop",
  N26 = "N26",
  Default = "Default",
}

export interface IBankAccount {
  bankId: number;
  iban: string;
  accountName: string | null;
  balance: number;
  currencyCode: string;
  bankName: BankName;
  isConsentValid: boolean;
  dateLastFetched: string;
  consentValidTo: string | null;
  isRenewalSupported: boolean;
  isRenewalPending: boolean;
}

export interface IBudgetBalanceStatisticsRequest {
  date: Date;
  bankAccountId?: number;
}

export interface IBudgetBalanceStatistics {
  balance: number;
  income: number;
  expense: number;
}

export interface IBankAccountsFilterResponse {
  bankAccounts: IBankAccountFilter[];
}

export interface IBankAccountFilter {
  bankAccountId: number;
  bankAccountName: string;
  isMain: boolean;
}

export interface IBudgetWeeklyExpenseStatisticsRequest {
  dateFrom: Date;
  bankAccountId?: number;
}

export interface IBudgetWeeklyExpenseStatistics {
  total: number;
  days: IBudgetWeeklyExpenseDay[];
}

export interface IBudgetWeeklyExpenseDay {
  date: string;
  dayOfTheWeek: number;
  total: number;
  categories: IBudgetWeeklyExpenseCategory[];
}

export enum BudgetCategory {
  Earnings = "earnings",
  Entertainment = "entertainment",
  FoodAndBeverages = "food-and-beverages",
  HealthSportsAndBeauty = "health-sports-and-beauty",
  Housing = "housing",
  PaymentsBetweenAccounts = "payments-between-accounts",
  PurchasesAndServices = "purchases-and-services",
  Transportation = "transportation",
  UtilityBills = "utility-bills",
  Uncategorized = "uncategorized",
  Custom1 = "custom1",
  Custom2 = "custom2",
  Custom3 = "custom3",
}

export interface IBudgetWeeklyExpenseCategory {
  categorySlug: BudgetCategory;
  total: number;
}

export interface IBudgetFinanceBreakdownRequest {
  dateFrom: Date;
  dateTo: Date;
  bankAccountId?: number;
  maxCategories?: number;
}

export interface IBudgetFinanceBreakdown {
  incomeTotal: number;
  expenseTotal: number;
  expenseCategories: IBudgetFinanceBreakdownCategory[];
}

export interface IBudgetFinanceBreakdownCategory {
  categorySlug: BudgetCategory;
  isEditable: boolean;
  total: number;
  transactionCategoryId: number;
  customerCategoryName?: string;
}

export enum BudgetTransactionDateFilterType {
  NoFiltering = "NoFiltering",
  Day = "Day",
  Month = "Month",
  Period = "Period",
}

export interface IBudgetTransactionsRequest {
  dateFilterType: BudgetTransactionDateFilterType;
  periodFrom?: Date;
  periodTo?: Date;
  date?: Date;
  bankAccountId?: number;
  transactionCategorySlug?: string;
  transactionTypeId?: TransactionType;
  searchText?: string;
  overrideEarningsCategoryId: boolean;
}

export interface IBudgetTransactions {
  incomeTotal: number;
  expenseTotal: number;
  transactions: IBudgetTransaction[];
}

export enum TransactionType {
  /**
   * Expense
   */
  Credit = "Credit",
  /**
   * Income
   */
  Debit = "Debit",
}

export interface IBudgetTransaction {
  bankAccountTransactionId: number;
  bankAccount: {
    bankId: number;
    bankName: BankName;
    bankAccountId: number;
    bankAccountName: string;
    iban: string;
  };
  payment: {
    partner: string;
    amount: number;
    transactionTypeId: TransactionType;
    transactionDate: string;
  };
  category: {
    transactionCategoryId: number;
    categorySlug: BudgetCategory;
    transactionSubcategoryId: number;
    subcategorySlug: BudgetCategory;
    customerCategoryName?: string;
    isEditable: boolean;
  };
}

export interface IBudgetTransactionAvailableCategoriesRequest {
  bankAccountTransactionId: number;
}

export interface IBudgetTransactionAvailableCategories {
  categories: IBudgetTransactionAvailableCategory[];
}

export interface IBudgetTransactionAvailableCategory {
  transactionTypeId: TransactionType;
  transactionCategoryId: number;
  categorySlug: string;
  transactionSubcategoryId: number | null;
  subcategorySlug: string | null;
  customerCategoryName?: string;
}

export interface IEditTransactionCategoryRequest {
  bankAccountTransactionId: number;
  transactionCategoryId: number;
  transactionSubcategoryId?: number | null;
  categoryModificationAction: string;
}

export interface IBudgetSettings {
  banks: IBudgetSettingsBank[];
  consents: IBudgetConsents;
}

export interface IBudgetSettingsBank {
  bankId: number;
  bankName: BankName;
  isConsentValid: boolean;
  consentValidTo: string | null;
  isRenewalSupported: boolean;
  bankAccounts: IBudgetSettingsBankAccount[];
}

export interface IBudgetSettingsBankAccount {
  bankAccountId: number;
  accountName: string | null;
  iban: string;
  isMain: boolean;
  isSelected: boolean;
}

export interface IBudgetConsents {
  consentedToStatistics: boolean;
  consentedToPrivacyPolicy: boolean;
}

export interface ISetAccountAsMainRequest {
  bankAccountId: number;
}

export interface IModifyAccountsRequest {
  bankAccounts: {
    bankAccountId: number;
    accountName?: string;
    isSelected: boolean;
  }[];
}

export interface IBudgetNewAccounts {
  banks: IBudgetNewBank[];
}

export interface IBudgetNewBank {
  bankName: BankName;
  bankAccounts: IBudgetNewBankAccount[];
}

export interface IBudgetNewBankAccount {
  bankAccountId: number;
  accountName: string;
  iban: string;
  isMain: boolean;
  isSelected: boolean;
}

export interface IMarkNewAccountsAsSeenRequest {
  bankAccountIds: number[];
}

export interface IRemoveAccountRequest {
  bankAccountId: number;
}

export interface IRemoveBankRequest {
  bankId: number;
}

export interface IAvailableBanks {
  banks: IAvailableBank[];
}

export interface IAvailableBank {
  bankId: number;
  bankName: BankName;
}

export interface IGetSavingsPlansRequest {
  maximumSavingsPlans?: number;
  savingsPlanStateId: SavingsPlanState;
}

export interface ISavingsPlans {
  savingsPlans: ISavingsPlan[];
}

export interface ISavingsPlanBankAccountsResponse {
  bankAccounts: ISavingsPlanBankAccount[];
}

export interface ISavingsPlanBankAccount {
  bankAccountId: number;
  bankAccountName: string;
  iban: string;
  bankId: number;
  bankName: BankName;
  swiftCode: string;
}

export enum SavingsPlanState {
  Active = "Active",
  Inactive = "Inactive",
}

export enum SavingsPlanDataProviderType {
  Manual = "Manual",
  BankAccountBalance = "BankAccountBalance",
}

export interface ISavingsPlan {
  savingsPlanId: number;
  plan: {
    savingsPlanStateId: SavingsPlanState;
    savingsPlanDataProviderTypeId: SavingsPlanDataProviderType;
    planName: string;
    planAmount: number;
    achievedAmount: number;
    dateCreated: string;
    endDate: string;
  };
  bankAccount?: {
    bankId: number;
    bankName: BankName;
    bankAccountId: number;
    bankAccountName: string;
    iban: string;
  };
}

export type TCreateSavingsPlanRequest =
  | {
      planName: string;
      planAmount: number;
      endDate?: string;
      savingsPlanDataProviderTypeId: SavingsPlanDataProviderType.BankAccountBalance;
      bankAccountId: number;
    }
  | {
      planName: string;
      planAmount: number;
      endDate?: string;
      savingsPlanDataProviderTypeId: SavingsPlanDataProviderType.Manual;
      achievedAmount: number;
    };

export type TModifySavingsPlanRequest =
  | {
      savingsPlanId: number;
      planName: string;
      planAmount: number;
      endDate?: string;
      savingsPlanDataProviderTypeId: SavingsPlanDataProviderType.BankAccountBalance;
      bankAccountId: number;
    }
  | {
      savingsPlanId: number;
      planName: string;
      planAmount: number;
      endDate?: string;
      savingsPlanDataProviderTypeId: SavingsPlanDataProviderType.Manual;
      achievedAmount: number;
    };

export interface IArchiveSavingsPlanRequest {
  savingsPlanId: number;
}

export interface IGetSavingsPlanPeriods {
  savingsPlanId: number;
  stepBack: number;
  elementCount: number;
}

export interface ISavingsPlanPeriods {
  stepsForwardAvailable: number;
  stepsBackAvailable: number;
  periods: ISavingsPlanPeriod[];
}

export interface ISavingsPlanPeriod {
  periodFrom: string;
  periodTo: string;
  periodAmount: number;
  planAmount: number;
}

export interface IIncrementSavingsPlanAchievedAmountRequest {
  savingsPlanId: number;
  incrementAmount: number;
}

export interface IRemoveSavingsPlanRequest {
  savingsPlanId: number;
}

export enum ExpensePlanInterval {
  Weekly = "Weekly",
  Monthly = "Monthly",
}

export interface IGetExpensePlansRequest {
  maximumExpensePlans?: number;
  expensePlanStateId: ExpensePlanState;
}

export interface IExpensePlans {
  expensePlans: IExpensePlan[];
}

export enum ExpensePlanState {
  Active = "Active",
  Inactive = "Inactive",
}

export interface IExpensePlan {
  expensePlanId: number;
  bankAccount?: {
    bankAccountId: number;
    bankAccountName: string;
    iban: string;
    bankId: number;
    bankName: BankName;
    swiftCode: string;
  };
  planName: string;
  planAmount: number;
  achievedAmount: number;
  transactionCategoryId: number;
  categorySlug: BudgetCategory;
  customerCategoryName?: string;
  expensePlanIntervalId: ExpensePlanInterval;
  expensePlanStateId: ExpensePlanState;
}

export interface IGetExpensePlanRequest {
  expensePlanId: number;
}

export interface IExtendedExpensePlan {
  expensePlanId: number;
  planName: string;
  planAmount: number;
  transactionCategoryId: number;
  categoryName: string;
  categorySlug: string;
  customerCategoryName?: string;
  expensePlanIntervalId: ExpensePlanInterval;
}

export interface IExpensePlanIntervals {
  intervals: string[];
}

export interface IExpensePlanCategories {
  transactionCategories: ITransactionCategory[];
}

export interface IGetExpensePlanTransactions {
  expensePlanId: number;
  pageIndex?: number;
  pageSize?: number;
}

export interface IExpensePlanTransactionsResponse {
  totalElements: number;
  elementsInPage: number;
  elements: IBudgetTransaction[];
}

export interface IGetExpensePlanPeriods {
  expensePlanId: number;
  stepBack: number;
  elementCount: number;
}

export interface IExpensePlanPeriods {
  stepsForwardAvailable: number;
  stepsBackAvailable: number;
  periods: IExpensePlanPeriod[];
}

export interface IExpensePlanPeriod {
  periodFrom: string;
  periodTo: string;
  periodAmount: number;
  planAmount: number;
}

export interface IArchiveExpensePlanRequest {
  expensePlanId: number;
}

export interface ITransactionCategory {
  transactionCategoryId: number;
  categoryName: string;
  transactionTypeId: TransactionType;
  categorySlug: BudgetCategory;
  customerCategoryName?: string;
}

export interface ITransactionModificationActionCategories {
  actions: string[];
}

export interface ICreateExpensePlanRequest {
  planName: string;
  planAmount: number;
  transactionCategoryId: number;
  expensePlanIntervalId: ExpensePlanInterval;
  bankAccountId?: number;
}

export interface IEditExpensePlanRequest {
  expensePlanId: number;
  planName: string;
  planAmount: number;
  bankAccountId?: number;
}

export interface IRemoveExpensePlanRequest {
  expensePlanId: number;
}

export interface ITransactionRequest {
  bankAccountTransactionId: number;
}

export interface ITransaction {
  bankAccountTransactionId: number;
  bankAccount: {
    bankId: number;
    bankName: BankName;
    bankAccountId: number;
    bankAccountName: string;
    iban: string;
  };
  payment: {
    amount: number;
    transactionNumber: string;
    transactionTypeId: TransactionType;
    transactionDate: string;
    partner: string;
    paymentDescription: string;
    associatedIban: string;
  };
  category: {
    initialCategory: {
      transactionCategoryId: number;
      categorySlug: BudgetCategory;
      transactionSubcategoryId: number;
      subcategorySlug: BudgetCategory;
      customerCategoryName?: string;
    };
    currentCategory: {
      transactionCategoryId: number;
      categorySlug: BudgetCategory;
      transactionSubcategoryId: number;
      subcategorySlug: BudgetCategory;
      customerCategoryName?: string;
    };
  };
}

export interface IDetailedBalanceStatisticsRequest {
  date?: Date;
  bankAccountId?: number;
  selectedInterval?: number;
}

export interface IDetailedBalanceStatistics {
  interval?: number;
  income: number;
  expense: number;
  dailyBalances: IDetailedBalanceStatisticsDay[];
}

export interface IDetailedBalanceStatisticsDay {
  date: string;
  income: number;
  expense: number;
  balance: number;
}

export interface IExpensePlanBankAccountsResponse {
  bankAccounts: IExpensePlanBankAccount[];
}

export interface IExpensePlanBankAccount {
  bankAccountId: number;
  bankAccountName: string;
  iban: string;
  bankId: number;
  bankName: BankName;
  swiftCode: string;
}

export interface IExpenseComparisonStatisticsPartnersRequest {
  date: Date;
  elementsCount: number;
  bankAccountId?: number;
  transactionCategoryId?: number;
}

export interface IExpenseComparisonStatisticsPartners {
  partners: IExpenseComparisonStatisticsPartner[];
}

export interface IExpenseComparisonStatisticsPartner {
  partner: string;
}

export interface IExpenseComparisonStatisticsExpenseCategories {
  expenseCategories: IExpenseComparisonStatisticsExpenseCategory[];
}

export interface IExpenseComparisonStatisticsExpenseCategory {
  transactionCategoryId: number;
  categorySlug: string;
  customerCategoryName?: string;
}

export interface IExpenseComparisonStatisticsRequest {
  date: Date;
  elementsCount: number;
  bankAccountId?: number;
  transactionCategoryId?: number;
  partnerName?: string;
}

export interface IExpenseComparisonStatistics {
  summary: IExpenseComparisonStatisticsSummary;
  periods: IExpenseComparisonStatisticsPeriod[];
}

export interface IExpenseComparisonStatisticsSummary {
  income: number;
  expense: number;
  averageExpense: number;
  expenseRatioToIncome: number;
}

export interface IExpenseComparisonStatisticsPeriod {
  periodFrom: string;
  periodTo: string;
  income: number;
  expense: number;
}

export interface ICreateTransactionCategoryRequest {
  categoryName: string;
}

export interface IEditCustomTransactionCategoryRequest {
  transactionCategoryId: number;
  categoryName: string;
}

export interface IGetCustomTransactionCategoryRequest {
  transactionCategoryId: number;
}

export interface IGetCustomTransactionCategoryResponse {
  transactionCategoryId: number;
  categorySlug: string;
  categoryName: string;
  hasTransactions: boolean;
  hasExpensePlans: boolean;
}

export enum RemoveBudgetTransactionCategoryType {
  NoAction = "NoAction",
  ReassignDefaultCategory = "ReassignDefaultCategory",
  ChangeCategory = "ChangeCategory",
}

export interface IRemoveCustomTransactionCategoryRequest {
  transactionCategoryId: number;
  removeType: RemoveBudgetTransactionCategoryType;
  newTransactionCategoryId?: number;
}

export enum CustomerPreferenceType {
  IncludeTransfersBetweenAccountsInStatistics = "IncludeTransfersBetweenAccountsInStatistics",
}

export interface IToggleCustomerBudgetPreferencesRequest {
  customerPreferenceTypeId: CustomerPreferenceType;
  isEnabled: boolean;
}

export interface ICustomerBudgetPreferences {
  customerPreferences: {
    customerPreferenceTypeId: CustomerPreferenceType;
    isEnabled: boolean;
  }[];
}
